import { VITE_ENV_NAME } from '@/config';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { isAxiosError } from 'axios';
import { ZodError } from 'zod';
import { excludeDepStack } from './error';
import { isProd } from './system';

type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'none';

const LogLevelWeights: { [key in LogLevel]: number } = {
  trace: 5,
  debug: 4,
  info: 3,
  warn: 2,
  error: 1,
  none: 0,
};

const convertToData = (
  data?: Record<string, unknown>,
  err?: Error | unknown,
) => {
  return err instanceof ZodError
    ? {
        ...data,
        details: err.flatten((issue) => {
          return {
            [issue.path.join('.')]: issue.message,
          };
        }),
        details_str: JSON.stringify(
          err.flatten((issue) => {
            return {
              [issue.path.join('.')]: issue.message,
            };
          }),
        ),
      }
    : isAxiosError(err)
      ? {
          ...data,
          details: err.response?.data,
          details_str: JSON.stringify(err.response?.data),
          error_code: err.code,
        }
      : err instanceof Error
        ? data
        : {
            ...data,
            error: err,
            details: undefined,
            details_str: undefined,
          };
};

const convertToObj = (
  data?: Record<string, unknown>,
  err?: Error | unknown,
) => {
  return {
    data: convertToData(data, err),
    http: isAxiosError(err)
      ? {
          method: err.config?.method?.toUpperCase(),
          url: `${err.config?.baseURL}${err.config?.url}`,
          status_code: err.response?.status,
        }
      : undefined,
  };
};

interface Logger {
  setLogLevel: (level: LogLevel) => void;
  trace: (message: string, data?: Record<string, unknown>, err?: Error) => void;
  debug: (message: string, data?: Record<string, unknown>, err?: Error) => void;
  info: (message: string, data?: Record<string, unknown>, err?: Error) => void;
  warn: (message: string, data?: Record<string, unknown>, err?: Error) => void;
  error: (message: string, err: Error, data?: Record<string, unknown>) => void;
}

class CustomLogger implements Logger {
  private logLevel: LogLevel;

  constructor() {
    this.logLevel =
      isProd() || VITE_ENV_NAME === 'mock'
        ? 'none'
        : ['training', 'stg'].includes(VITE_ENV_NAME)
          ? 'warn'
          : 'trace';
  }

  setLogLevel(level: LogLevel) {
    this.logLevel = level;
  }

  trace(message: string, data?: Record<string, unknown>) {
    const level = 'trace';
    if (this._shouldLog(level)) {
      // biome-ignore lint/suspicious/noConsole: (migrate from ESLint)
      console.trace(this._formatMessage(level, message), data);
    }
  }

  debug(message: string, data?: Record<string, unknown>, err?: Error) {
    const level = 'debug';
    if (this._shouldLog(level)) {
      // biome-ignore lint/suspicious/noConsole: (migrate from ESLint)
      console.debug(this._formatMessage(level, message), { data, err });
    }
  }

  info(message: string, data?: Record<string, unknown>, err?: Error) {
    const level = 'info';
    if (this._shouldLog(level)) {
      // biome-ignore lint/suspicious/noConsole: (migrate from ESLint)
      console.info(this._formatMessage(level, message), { ...data, err });
    }
    if (err?.stack) {
      err.stack = excludeDepStack(err.stack);
    }
    datadogLogs.logger.info(message, data, err);
  }

  warn(message: string, data?: Record<string, unknown>, err?: Error) {
    const level = 'warn';
    const obj = convertToObj(data, err);
    if (this._shouldLog(level)) {
      // biome-ignore lint/suspicious/noConsole: (migrate from ESLint)
      console.warn(this._formatMessage(level, message), { ...obj, err });
    }
    if (err?.stack) {
      err.stack = excludeDepStack(err.stack);
    }
    datadogLogs.logger.warn(message, obj, err);
  }

  error(message: string, err: Error | unknown, data?: Record<string, unknown>) {
    const level = 'error';
    const newErr =
      err instanceof Error ? err : new Error(message || 'unknown error');
    const obj = convertToObj(data, err);

    if (this._shouldLog(level)) {
      // WARNING: If console.error is used, duplicated logs are transferred to Datadog, so console.warn is used.
      // biome-ignore lint/suspicious/noConsole: (migrate from ESLint)
      console.warn(this._formatMessage(level, message || newErr.message), {
        ...obj,
        err: newErr,
      });
    }

    newErr.stack = excludeDepStack(newErr.stack);
    datadogLogs.logger.error(message, obj, newErr);
    datadogRum.addError(newErr, obj);
  }

  private _shouldLog(level: LogLevel): boolean {
    return LogLevelWeights[level] <= LogLevelWeights[this.logLevel];
  }

  private _formatMessage(level: LogLevel, message: string) {
    return `[${level.toUpperCase()}]: ${message}`;
  }
}

export const logger = new CustomLogger();
